import React from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';
import { ConnectionPool } from '@gitbook/util-websocket';

import {
    GitBookAgentClient,
    type GitBookAgentClientOptions,
    type GitBookAgentClientState,
} from './GitBookAgentClient';

type AgentPool = ConnectionPool<GitBookAgentClient>;

const AgentContext = React.createContext<AgentPool | null>(null);

export function AgentProvider({ children }: { children: React.ReactNode }) {
    const pool = React.useMemo(
        () =>
            new ConnectionPool<GitBookAgentClient>({
                onDestroy: (agent) => {
                    agent.disconnect();
                },
                ttl: 1000 * 60 * 5,
            }),
        []
    );

    return <AgentContext.Provider value={pool}>{children}</AgentContext.Provider>;
}

/**
 * Return a function to imperatively create a new agent client.
 */
export function useGetAgent() {
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useGetAgent should be used within an AgentProvider');
    }

    return React.useCallback(
        <Rpc extends {} = {}>(options: GitBookAgentClientOptions): GitBookAgentClient<Rpc> => {
            // @ts-expect-error - ConnectionPool is not generic
            const poolAs = pool as ConnectionPool<GitBookClient<Rpc>>;
            return poolAs.get(options.pathname, () => {
                const newAgent = new GitBookAgentClient<Rpc>(options);
                newAgent.connect();

                return newAgent;
            });
        },
        [pool]
    );
}

/**
 * Hook to connect to a content agent.
 */
export function useAgent<Rpc extends {} = {}>(
    options: GitBookAgentClientOptions
): GitBookAgentClient<Rpc> {
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useAgent should be used within an AgentProvider');
    }
    const getAgent = useGetAgent();
    const getToken = useEventCallback(options.getToken);

    const agent = React.useMemo(() => {
        return getAgent<Rpc>({
            getToken,
            pathname: options.pathname,
        });
    }, [getAgent, options.pathname, getToken]);

    React.useEffect(() => {
        return pool.keepAlive(agent);
    }, [pool, agent]);

    return agent;
}

/**
 * Hook to get the state of the agent.
 */
export function useAgentState<
    MapState extends undefined | ((state: GitBookAgentClientState) => any) = undefined,
>(
    // Because of https://github.com/cloudflare/capnweb/issues/53
    // we need to use a very basic type to avoid type errors
    agent: {
        listen: (event: 'state', callback: (state: GitBookAgentClientState) => void) => () => void;
        state: GitBookAgentClientState;
    },
    mapState?: MapState
): MapState extends (state: GitBookAgentClientState) => any
    ? ReturnType<MapState>
    : GitBookAgentClientState {
    return React.useSyncExternalStore(
        React.useCallback(
            (callback) => {
                return agent.listen('state', callback);
            },
            [agent]
        ),
        React.useCallback(() => {
            return mapState ? mapState(agent.state) : agent.state;
        }, [agent, mapState])
    );
}
